import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api, { getImageUrl, API_BASE } from '../../utils/api';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

interface Bookmark {
    _id: string;
    name: string;
    location?: string;
    city?: string;
    address?: string;
    priceRange?: string;
    minPrice?: number;
    maxPrice?: number;
    featuredImage?: string;
    type?: string; 
    propertyType?: string; 
    itemType: 'property' | 'project';
    slug?: string;
}

export default function BrokerWishlist() {
    const { token, user } = useAuth();
    const { theme } = useTheme();
    const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
    const [loading, setLoading] = useState(true);
    const [credits, setCredits] = useState(0);

    // Unlock Modal States
    const [unlockingProject, setUnlockingProject] = useState<{ id: string, name: string } | null>(null);
    const [otpStep, setOtpStep] = useState<'confirm' | 'otp' | 'success'>('confirm');
    const [otp, setOtp] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchWishlist = async () => {
        if (!token) return;
        try {
            setLoading(true);
            const data = await api.getWishlist(token);
            const combined: Bookmark[] = [
                ...(data.wishlist || []).map((p: any) => ({ ...p, itemType: 'property' })),
                ...(data.projectWishlist || []).map((p: any) => ({ ...p, itemType: 'project' }))
            ];
            setBookmarks(combined);
            const userRes = await fetch(`${API_BASE}/users/me`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const userData = await userRes.json();
            setCredits(userData.remainingInquiries || 0);
        } catch (error) {
            console.error("Failed to fetch wishlist:", error);
            toast.error("Failed to load your asset vault");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWishlist();
    }, [token]);

    const handleGetLeads = (projectId: string, projectName: string) => {
        setUnlockingProject({ id: projectId, name: projectName });
        setOtpStep('confirm');
        setOtp('');
    };

    const handleSendOTP = async () => {
        if (!unlockingProject) return;
        setIsProcessing(true);
        try {
            const res = await fetch(`${API_BASE}/leads/unlock/send-otp`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                toast.success(data.message);
                setOtpStep('otp');
            } else {
                toast.error(data.message);
            }
        } catch (err) {
            toast.error('Failed to send verification code');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleVerifyOTP = async () => {
        if (!unlockingProject || !otp) return;
        setIsProcessing(true);
        try {
            const res = await fetch(`${API_BASE}/leads/unlock/verify`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ projectId: unlockingProject.id, otp })
            });
            const data = await res.json();
            if (res.ok) {
                toast.success(data.message);
                setCredits(data.remainingInquiries);
                setOtpStep('success');
                setTimeout(() => {
                    setUnlockingProject(null);
                }, 2000);
            } else {
                toast.error(data.message);
            }
        } catch (err) {
            toast.error('Verification failed');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRemove = async (itemId: string, itemType: 'property' | 'project') => {
        if (!token) return;
        try {
            const res = await api.toggleWishlist(token, itemId, itemType);
            if (!res.isSaved) {
                setBookmarks(prev => prev.filter(b => b._id !== itemId));
                toast.info("Removed from your vault");
            }
        } catch (error) {
            toast.error("Failed to update vault");
        }
    };

    const formatPrice = (b: Bookmark) => {
        if (b.priceRange) return b.priceRange;
        if (b.minPrice) return `₹${b.minPrice.toLocaleString()}`;
        return 'Price On Request';
    };

    return (
        <UserDashboardLayout>
            <div className="max-w-7xl mx-auto py-8">
                {/* Page Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-12 px-4 animate__animated animate__fadeInDown">
                    <div>
                        <h1 className="text-4xl md:text-5xl font-black text-[var(--text-primary)] mb-3 tracking-tighter uppercase italic">Asset Vault</h1>
                        <p className="text-[var(--text-secondary)] font-black text-xs uppercase tracking-[0.2em] opacity-70">Curating high-potential cryptographic property portfolios.</p>
                    </div>
                    
                    {/* Credits Badge */}
                    <div className="glass-card px-8 py-5 border-[var(--purple)]/10 flex items-center gap-6 shadow-2xl relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-r from-[var(--purple)]/5 to-transparent"></div>
                        <div className="w-14 h-14 bg-[var(--purple)]/10 rounded-2xl flex items-center justify-center text-[var(--purple)] border border-[var(--purple)]/10 shadow-inner group-hover:scale-110 transition-transform">
                            <i className="fa-solid fa-bolt-lightning text-xl"></i>
                        </div>
                        <div className="relative z-10">
                            <p className="text-[10px] font-black uppercase text-[var(--text-secondary)] tracking-[0.2em] mb-1.5 opacity-60">Unlock Credits</p>
                            <h4 className="text-3xl font-black text-[var(--text-primary)] leading-none italic">{credits}</h4>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="py-40 text-center">
                        <div className="w-16 h-16 border-4 border-[var(--purple)] border-t-transparent rounded-full animate-spin mx-auto mb-8 shadow-2xl"></div>
                        <p className="text-[var(--text-secondary)] font-black uppercase tracking-[0.4em] text-[10px] animate-pulse">Syncing Secure Vault...</p>
                    </div>
                ) : bookmarks.length === 0 ? (
                    <div className="py-40 glass-card mx-4 border-dashed border-[var(--purple)]/20 text-center">
                        <div className="w-28 h-28 bg-[var(--purple)]/5 text-[var(--purple)]/20 rounded-[2.5rem] flex items-center justify-center mx-auto mb-10 shadow-inner border border-[var(--purple)]/10">
                            <i className="fa-solid fa-box-open text-5xl"></i>
                        </div>
                        <h2 className="text-[var(--text-primary)] font-black text-3xl mb-4 tracking-tighter uppercase italic">Vault Decrypted - Empty</h2>
                        <p className="text-[var(--text-secondary)] font-bold max-w-sm mx-auto uppercase text-[10px] tracking-widest opacity-60 italic leading-relaxed">No high-value assets identified in your secure collection nodes.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-20 px-4">
                        <AnimatePresence>
                            {bookmarks.map((item, idx) => (
                                <motion.div 
                                    key={item._id}
                                    layout
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="group glass-card overflow-hidden border-[var(--purple)]/10 hover:border-[var(--purple)]/40 transition-all shadow-xl flex flex-col h-full relative"
                                >
                                    <div className="relative h-64 overflow-hidden bg-[var(--bg-main)]">
                                        <img 
                                            src={getImageUrl(item.featuredImage)} 
                                            alt={item.name} 
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" 
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                        <div className="absolute top-5 left-5 flex gap-2">
                                            <span className={`px-4 py-2 rounded-2xl text-[9px] font-black uppercase tracking-[0.2em] shadow-2xl border backdrop-blur-xl ${item.itemType === 'project' ? 'bg-[var(--gold)]/80 text-black border-[var(--gold)]/20' : 'bg-[var(--purple)]/80 text-white border-[var(--purple)]/20'}`}>
                                                {item.itemType} Node
                                            </span>
                                        </div>
                                        <button 
                                            onClick={() => handleRemove(item._id, item.itemType)}
                                            className="absolute top-5 right-5 w-12 h-12 rounded-2xl bg-black/40 backdrop-blur-xl text-white/50 hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 z-10 border border-white/10 active:scale-90 shadow-2xl"
                                        >
                                            <i className="fa-solid fa-trash-can text-lg"></i>
                                        </button>
                                    </div>
                                    
                                    <div className="p-8 flex flex-col flex-grow relative">
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-[var(--purple)]/5 rounded-bl-full -z-10 group-hover:bg-[var(--purple)]/10 transition-colors"></div>
                                        
                                        <h3 className="text-2xl font-black text-[var(--text-primary)] mb-3 truncate group-hover:text-[var(--purple)] transition-colors tracking-tighter uppercase italic">
                                            {item.name}
                                        </h3>
                                        <div className="flex items-center gap-3 text-[var(--text-secondary)] font-black text-[10px] mb-10 uppercase tracking-widest opacity-60">
                                            <i className="fa-solid fa-location-dot text-[var(--purple)] text-base"></i>
                                            <span className="truncate">{item.location || item.city || item.address || 'Geo-Restricted'}</span>
                                        </div>

                                        <div className="mt-auto pt-8 border-t border-[var(--purple)]/10 flex flex-col gap-8">
                                            <div className="flex items-center justify-between">
                                                <div className="text-2xl font-black text-[var(--purple)] italic tracking-tighter">
                                                    {formatPrice(item)}
                                                </div>
                                                <Link 
                                                    to={item.itemType === 'project' ? `/project-details/${item.slug || item._id}` : `/property-details/${item._id}`}
                                                    className="w-12 h-12 rounded-2xl bg-[var(--purple)]/5 text-[var(--purple)] hover:bg-[var(--purple)] hover:text-white transition-all flex items-center justify-center active:scale-90 border border-[var(--purple)]/10 shadow-lg"
                                                >
                                                    <i className="fa-solid fa-expand text-lg"></i>
                                                </Link>
                                            </div>

                                            <div className="flex items-center gap-4">
                                                <button 
                                                    onClick={() => handleGetLeads(item._id, item.name)}
                                                    disabled={unlockingProject?.id === item._id}
                                                    className="btn-purple flex-1 py-4 text-[10px] no-underline shadow-2xl"
                                                >
                                                    {unlockingProject?.id === item._id ? (
                                                        <i className="fa-solid fa-circle-notch animate-spin"></i>
                                                    ) : (
                                                        'Unlock Data Stream'
                                                    )}
                                                </button>
                                                <button 
                                                    onClick={() => handleRemove(item._id, item.itemType)}
                                                    className="w-14 h-14 rounded-2xl bg-[var(--purple)]/5 text-[var(--text-secondary)] hover:bg-rose-500/10 hover:text-rose-500 transition-all active:scale-90 flex items-center justify-center border border-[var(--purple)]/10 shadow-lg"
                                                >
                                                    <i className="fa-solid fa-box-archive text-xl"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* UNLOCK VERIFICATION MODAL */}
            <AnimatePresence>
                {unlockingProject && (
                    <div className="fixed inset-0 z-[2000] flex items-center justify-center px-4">
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => !isProcessing && setUnlockingProject(null)}
                            className="absolute inset-0 bg-[#0d0221]/60 backdrop-blur-xl"
                        />
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.9, y: 50 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 50 }}
                            className="relative w-full max-w-lg glass-card p-10 md:p-14 border-[var(--purple)]/20 shadow-[0_50px_100px_-20px_rgba(125,95,255,0.4)]"
                        >
                            <div className="absolute -top-12 -left-12 w-48 h-48 bg-[var(--purple)]/10 rounded-full blur-3xl -z-10"></div>
                            
                            <div className="text-center relative z-10">
                                {otpStep === 'confirm' && (
                                    <>
                                        <div className="w-24 h-24 bg-[var(--purple)]/10 text-[var(--purple)] rounded-[2rem] flex items-center justify-center mx-auto mb-10 shadow-inner border border-[var(--purple)]/10 group-hover:scale-110 transition-transform">
                                            <i className="fa-solid fa-unlock-keyhole text-4xl"></i>
                                        </div>
                                        <h3 className="text-3xl font-black text-[var(--text-primary)] mb-4 tracking-tighter uppercase italic">Decrypt Leads?</h3>
                                        <div className="bg-[var(--purple)]/5 p-6 rounded-[2rem] mb-12 border border-[var(--purple)]/10">
                                            <p className="text-[var(--text-secondary)] font-bold text-xs mb-3 uppercase tracking-widest opacity-60">Selected Asset Node</p>
                                            <h4 className="text-sm font-black text-[var(--text-primary)] uppercase mb-4">{unlockingProject.name}</h4>
                                            <div className="h-px bg-[var(--purple)]/10 w-12 mx-auto mb-4"></div>
                                            <p className="text-[var(--purple)] font-black text-xs uppercase tracking-widest italic">Protocol Cost: 1 Credit</p>
                                        </div>
                                        <div className="flex flex-col gap-4">
                                            <button 
                                                onClick={handleSendOTP}
                                                disabled={isProcessing}
                                                className="btn-purple py-5 text-[11px] shadow-2xl no-underline"
                                            >
                                                {isProcessing ? <i className="fa-solid fa-circle-notch animate-spin me-2"></i> : null}
                                                Initialize Verification
                                            </button>
                                            <button 
                                                onClick={() => setUnlockingProject(null)}
                                                disabled={isProcessing}
                                                className="py-4 text-[var(--text-secondary)] font-black text-[10px] uppercase tracking-[0.3em] hover:text-[var(--text-primary)] transition-colors opacity-50"
                                            >
                                                Abort Sequence
                                            </button>
                                        </div>
                                    </>
                                )}

                                {otpStep === 'otp' && (
                                    <>
                                        <div className="w-24 h-24 bg-[var(--gold)]/10 text-[var(--gold)] rounded-[2rem] flex items-center justify-center mx-auto mb-10 shadow-inner border border-[var(--gold)]/10">
                                            <i className="fa-solid fa-fingerprint text-4xl"></i>
                                        </div>
                                        <h3 className="text-3xl font-black text-[var(--text-primary)] mb-4 tracking-tighter uppercase italic">Authorization</h3>
                                        <p className="text-[var(--text-secondary)] font-bold text-xs mb-12 px-6 uppercase tracking-widest opacity-60 leading-loose">
                                            Enter the 6-digit cryptographic key sent to your verified node to authorize decryption.
                                        </p>
                                        <input 
                                            type="text" 
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            placeholder="XXXXXX"
                                            className="w-full bg-[var(--bg-main)] border-2 border-[var(--purple)]/20 rounded-3xl py-6 text-center text-5xl font-black tracking-[0.5em] text-[var(--purple)] mb-12 focus:border-[var(--purple)] focus:ring-0 transition-all outline-none shadow-inner"
                                        />
                                        <div className="flex flex-col gap-4">
                                            <button 
                                                onClick={handleVerifyOTP}
                                                disabled={isProcessing || otp.length < 6}
                                                className="btn-purple py-5 text-[11px] shadow-2xl no-underline bg-[var(--purple)]"
                                            >
                                                {isProcessing ? <i className="fa-solid fa-circle-notch animate-spin me-2"></i> : null}
                                                Finalize Authorization
                                            </button>
                                            <button 
                                                onClick={() => setOtpStep('confirm')}
                                                disabled={isProcessing}
                                                className="text-[9px] font-black text-[var(--text-secondary)] uppercase tracking-[0.3em] hover:text-[var(--purple)] transition-all opacity-50"
                                            >
                                                Return to Node Selection
                                            </button>
                                        </div>
                                    </>
                                )}

                                {otpStep === 'success' && (
                                    <>
                                        <div className="w-28 h-28 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-10 shadow-2xl border-4 border-emerald-500/20 animate__animated animate__bounceIn">
                                            <i className="fa-solid fa-check-double text-5xl"></i>
                                        </div>
                                        <h3 className="text-3xl font-black text-[var(--text-primary)] mb-4 tracking-tighter uppercase italic">Stream Unlocked</h3>
                                        <p className="text-[var(--text-secondary)] font-bold text-xs px-12 uppercase tracking-[0.2em] opacity-60 leading-loose">
                                            Cryptographic data stream successfully synchronized with your lead database.
                                        </p>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </UserDashboardLayout>
    );
}
